// AIDEV-NOTE: `part_interactions.attributes[].content` is a string column, but two writers put
// JSON arrays in it: the v2 pipeline's extras fold (sessionPipelineV2 JSON.stringify's any
// non-string part field into content) and the structured chip/card editors on the session part
// detail (EmotionsField / RelationshipsField persist the full array as JSON). Both shapes are
// legitimate and both are already in production data, so every OTHER consumer — plain-text detail
// rows, the print view, LLM prompt builders — must normalize before rendering, or the user sees
// a raw `["vigilance","wariness"]` blob. Prose content is returned untouched.

interface RawRelationshipShape {
    type?: unknown;
    description?: unknown;
}

/**
 * A relationship entry after narrowing: `type`/`description` are guaranteed strings (possibly
 * empty), and any extra keys the writer stored (sourcePartId, targetPartId, …) are preserved so
 * the structured editor can serialize the entry back without dropping fields.
 */
export interface RelationshipAttribute {
    type: string;
    description: string;
    sourcePartId?: string;
    targetPartId?: string;
}

/**
 * Parse `content` as a JSON array of unknowns, or return null when it is ordinary prose.
 *
 * Only strings that start with "[" are even attempted, so legacy v1 plain text (and prose that
 * merely contains a bracket) never pays the JSON.parse cost or risks a partial parse.
 *
 * AIDEV-NOTE: Returns `unknown[]` on purpose — the JSON is model output, so nothing guarantees
 * its element shape. Consumers that hand elements to a renderer must narrow first; use
 * parseStringArrayAttribute / parseRelationshipArrayAttribute rather than casting, or a stray
 * object lands somewhere expecting a string and throws on `.toLowerCase()`.
 */
export function parseJsonArrayAttribute(
    content: string | null | undefined,
): unknown[] | null {
    if (!content) return null;
    const trimmed = content.trim();
    if (!trimmed.startsWith("[")) return null;
    try {
        const parsed: unknown = JSON.parse(trimmed);
        return Array.isArray(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

function isRelationshipShape(item: unknown): item is RawRelationshipShape {
    return (
        typeof item === "object" &&
        item !== null &&
        ("type" in item || "description" in item)
    );
}

/** Coerce a raw relationship entry to string fields, preserving any extra keys for round-trip. */
function narrowRelationship(item: RawRelationshipShape): RelationshipAttribute {
    return {
        ...item,
        type: typeof item.type === "string" ? item.type : "",
        description:
            typeof item.description === "string" ? item.description : "",
    };
}

/**
 * Parse `content` as an array of non-empty strings (emotions, expressions, quotes).
 *
 * Non-string entries are dropped. Returns null for prose, so callers keep their plain-text
 * fallback; an array that parses but holds nothing usable yields an empty array, which reads
 * as "structured but empty" rather than "not structured".
 */
export function parseStringArrayAttribute(
    content: string | null | undefined,
): string[] | null {
    const parsed = parseJsonArrayAttribute(content);
    if (!parsed) return null;
    return parsed.filter(
        (item): item is string =>
            typeof item === "string" && item.trim() !== "",
    );
}

/** Parse `content` as an array of relationship objects, dropping entries of any other shape. */
export function parseRelationshipArrayAttribute(
    content: string | null | undefined,
): RelationshipAttribute[] | null {
    const parsed = parseJsonArrayAttribute(content);
    if (!parsed) return null;
    return parsed
        .filter(isRelationshipShape)
        .map(narrowRelationship)
        .filter(
            (item) => item.type.trim() !== "" || item.description.trim() !== "",
        );
}

/**
 * Render one JSON-array element as human-readable text.
 *
 * Strings pass through and relationship objects become "type — description". Everything else —
 * including bare numbers and booleans — is dropped (returns null) rather than stringified: these
 * attributes are prose fields, so a scalar array is pipeline metadata that leaked into a display
 * key, not something to show a client.
 */
function formatArrayItem(item: unknown): string | null {
    if (typeof item === "string") return item.trim() || null;
    if (isRelationshipShape(item)) {
        const { type, description } = narrowRelationship(item);
        if (type.trim() && description.trim()) {
            return `${type.trim()} — ${description.trim()}`;
        }
        return type.trim() || description.trim() || null;
    }
    return null;
}

/**
 * Normalize an attribute's stored `content` into text safe to render or feed to a prompt.
 *
 * - prose → returned unchanged
 * - `["a","b"]` → `"a, b"`
 * - `[{type,description}, …]` → `"type — description; …"`
 * - `[]` (or an array with nothing renderable) → `emptyFallback`, default `""`
 * - blank/whitespace-only → `emptyFallback`, so it hides like any other empty attribute
 *
 * Callers that treat "Unclear" as the empty sentinel (the hide-unclear detail rows) pass it as
 * `emptyFallback` so an empty array collapses into the same dim/hidden path as a blank field.
 */
export function formatAttributeContent(
    content: string | null | undefined,
    { emptyFallback = "" }: { emptyFallback?: string } = {},
): string {
    if (!content?.trim()) return emptyFallback;
    const parsed = parseJsonArrayAttribute(content);
    if (!parsed) return content;

    const parts = parsed
        .map(formatArrayItem)
        .filter((item): item is string => Boolean(item));
    if (parts.length === 0) return emptyFallback;

    // Relationship-style entries are full clauses, so semicolons keep them readable; short
    // string tags (emotions, expressions) read as a plain comma list. Decided from the ELEMENT
    // SHAPE, not the rendered text — a string tag that happens to contain an em-dash is still a
    // tag, and must not flip the whole list to semicolons.
    const separator = parsed.some(isRelationshipShape) ? "; " : ", ";
    return parts.join(separator);
}
